// user-portal.jsx
"use client";

import React, { useState, useEffect } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { PublicKey, LAMPORTS_PER_SOL, Transaction } from "@solana/web3.js";
import {
  getAccount,
  getAssociatedTokenAddress,
  getMint,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createTransferCheckedInstruction,
  createAssociatedTokenAccountInstruction,
} from "@solana/spl-token";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import {
  Wallet,
  Send,
  DollarSign,
  Gift,
  ExternalLink,
  TrendingUp,
  Coins,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { AirdropApplicationForm } from "./AirdropApplicationForm";

const UserPortal = ({ mintAddress }) => {
  const { publicKey, connected, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { toast } = useToast();

  const [tokenBalance, setTokenBalance] = useState(null);
  const [solBalance, setSolBalance] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferAddress, setTransferAddress] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refreshBalances = async () => {
    if (!publicKey || !connection || !connected) return;

    setRefreshing(true);
    try {
      // Get SOL balance
      const solBalanceLamports = await connection.getBalance(publicKey);
      const solBalanceSOL = solBalanceLamports / LAMPORTS_PER_SOL;

      setSolBalance({
        balance: solBalanceSOL,
        usdValue: solBalanceSOL * 100, // Mock USD price
      });

      // Get token info and balance
      try {
        const mintPubkey = new PublicKey(mintAddress);

        // Use Token Extensions Program ID
        const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;

        // Get mint info to determine actual decimals and supply
        const mintInfo = await getMint(
          connection,
          mintPubkey,
          "confirmed",
          TOKEN_EXTENSIONS_PROGRAM_ID
        );

        // Set token info including total supply
        setTokenInfo({
          supply: Number(mintInfo.supply) / Math.pow(10, mintInfo.decimals),
          decimals: mintInfo.decimals,
        });

        const tokenAccountAddress = await getAssociatedTokenAddress(
          mintPubkey,
          publicKey,
          false,
          TOKEN_EXTENSIONS_PROGRAM_ID,
          ASSOCIATED_TOKEN_PROGRAM_ID
        );

        const tokenAccount = await getAccount(
          connection,
          tokenAccountAddress,
          "confirmed",
          TOKEN_EXTENSIONS_PROGRAM_ID
        );
        const balance =
          Number(tokenAccount.amount) / Math.pow(10, mintInfo.decimals);

        setTokenBalance({
          balance,
          usdValue: balance * 0.5, // Mock USD price
        });
      } catch (error) {
        console.error("Error fetching token info and balance:", error);
        // Token account doesn't exist yet, but we can still get token info
        try {
          const mintPubkey = new PublicKey(mintAddress);
          const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;
          const mintInfo = await getMint(
            connection,
            mintPubkey,
            "confirmed",
            TOKEN_EXTENSIONS_PROGRAM_ID
          );

          setTokenInfo({
            supply: Number(mintInfo.supply) / Math.pow(10, mintInfo.decimals),
            decimals: mintInfo.decimals,
          });
        } catch (mintError) {
          console.error("Error fetching mint info:", mintError);
        }

        setTokenBalance({
          balance: 0,
          usdValue: 0,
        });
      }

      toast({
        title: "Balances updated",
        description: "Your wallet balances have been refreshed",
        variant: "default",
      });
    } catch (error) {
      console.error("Error refreshing balances:", error);
      toast({
        title: "Error refreshing balances",
        description: "Failed to refresh your wallet balances",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const loadBalances = async () => {
      if (!publicKey || !connection || !connected) return;

      setLoading(true);
      try {
        // Get SOL balance
        const solBalanceLamports = await connection.getBalance(publicKey);
        const solBalanceSOL = solBalanceLamports / LAMPORTS_PER_SOL;

        setSolBalance({
          balance: solBalanceSOL,
          usdValue: solBalanceSOL * 100, // Mock USD price
        });

        // Get token balance
        try {
          const mintPubkey = new PublicKey(mintAddress);

          // Use Token Extensions Program ID
          const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;

          // Get mint info to determine actual decimals and supply
          const mintInfo = await getMint(
            connection,
            mintPubkey,
            "confirmed",
            TOKEN_EXTENSIONS_PROGRAM_ID
          );

          // Set token info including total supply
          setTokenInfo({
            supply: Number(mintInfo.supply) / Math.pow(10, mintInfo.decimals),
            decimals: mintInfo.decimals,
          });

          const tokenAccountAddress = await getAssociatedTokenAddress(
            mintPubkey,
            publicKey,
            false,
            TOKEN_EXTENSIONS_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
          );

          const tokenAccount = await getAccount(
            connection,
            tokenAccountAddress,
            "confirmed",
            TOKEN_EXTENSIONS_PROGRAM_ID
          );
          const balance =
            Number(tokenAccount.amount) / Math.pow(10, mintInfo.decimals);

          setTokenBalance({
            balance,
            usdValue: balance * 0.5, // Mock USD price
          });
        } catch (error) {
          console.error("Error fetching token balance:", error);
          // Token account doesn't exist yet, but we can still get token info
          try {
            const mintPubkey = new PublicKey(mintAddress);
            const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;
            const mintInfo = await getMint(
              connection,
              mintPubkey,
              "confirmed",
              TOKEN_EXTENSIONS_PROGRAM_ID
            );

            setTokenInfo({
              supply: Number(mintInfo.supply) / Math.pow(10, mintInfo.decimals),
              decimals: mintInfo.decimals,
            });
          } catch (mintError) {
            console.error("Error fetching mint info:", mintError);
          }

          setTokenBalance({
            balance: 0,
            usdValue: 0,
          });
        }
      } catch (error) {
        console.error("Error loading balances:", error);
        toast({
          title: "Error loading balances",
          description: "Failed to load your wallet balances",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (connected) {
      loadBalances();
    }
  }, [connected, publicKey, mintAddress, connection, toast]);

  const handleJupiterSwap = () => {
    // Open Jupiter in new tab
    window.open("https://www.orca.so/pools/Cnjcb8oVAWxW6jKY4jrb42BdJKg82pngzu6dDJ6sdG1T", "_blank");
    toast({
      title: "Opening Jupiter",
      description: "Redirecting to Jupiter for token swaps",
      variant: "default",
    });
  };

  const handleClaimRewards = () => {
    // Dummy function for staking rewards
    toast({
      title: "Staking coming soon",
      description:
        "Staking rewards feature will be available in the next update",
      variant: "default",
    });
  };

  const handleTransfer = async () => {
    if (!publicKey || !sendTransaction) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet first",
        variant: "destructive",
      });
      return;
    }

    const amount = parseFloat(transferAmount);
    if (!amount || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount to transfer",
        variant: "destructive",
      });
      return;
    }

    if (!transferAddress) {
      toast({
        title: "Invalid address",
        description: "Please enter a valid recipient address",
        variant: "destructive",
      });
      return;
    }

    // Validate recipient address format
    try {
      new PublicKey(transferAddress);
    } catch (error) {
      toast({
        title: "Invalid address",
        description: "Please enter a valid Solana wallet address",
        variant: "destructive",
      });
      return;
    }

    // Check if user has enough balance
    if (!tokenBalance || amount > tokenBalance.balance) {
      toast({
        title: "Insufficient balance",
        description: `You don't have enough tokens. Available: ${
          tokenBalance?.balance || 0
        }`,
        variant: "destructive",
      });
      return;
    }

    setTransferring(true);
    try {
      const mintPubkey = new PublicKey(mintAddress);
      const recipientPubkey = new PublicKey(transferAddress);

      // Use Token Extensions Program ID
      const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;

      console.log("Starting transfer transaction...");
      console.log("Mint address:", mintAddress);
      console.log("Recipient address:", transferAddress);
      console.log("Amount:", amount);

      // Get mint info to determine decimals
      const mintInfo = await getMint(
        connection,
        mintPubkey,
        "confirmed",
        TOKEN_EXTENSIONS_PROGRAM_ID
      );
      console.log("Mint info:", mintInfo);

      // Get sender's token account
      const senderTokenAccount = await getAssociatedTokenAddress(
        mintPubkey,
        publicKey,
        false,
        TOKEN_EXTENSIONS_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      // Get recipient's token account
      const recipientTokenAccount = await getAssociatedTokenAddress(
        mintPubkey,
        recipientPubkey,
        false,
        TOKEN_EXTENSIONS_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      console.log("Sender token account:", senderTokenAccount.toString());
      console.log("Recipient token account:", recipientTokenAccount.toString());

      const transaction = new Transaction();

      // Check if recipient's token account exists, if not create it
      let needToCreateAccount = false;
      try {
        const recipientAccountInfo = await connection.getAccountInfo(
          recipientTokenAccount
        );
        if (!recipientAccountInfo) {
          needToCreateAccount = true;
        }
      } catch (error) {
        needToCreateAccount = true;
      }

      if (needToCreateAccount) {
        console.log("Creating recipient token account...");
        transaction.add(
          createAssociatedTokenAccountInstruction(
            publicKey, // payer
            recipientTokenAccount,
            recipientPubkey, // owner
            mintPubkey,
            TOKEN_EXTENSIONS_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
          )
        );
      }

      // Convert amount to token units using actual decimals
      const transferAmountInUnits = Math.floor(
        amount * Math.pow(10, mintInfo.decimals)
      );
      console.log("Transfer amount in units:", transferAmountInUnits);

      // Add transfer instruction
      transaction.add(
        createTransferCheckedInstruction(
          senderTokenAccount,
          mintPubkey,
          recipientTokenAccount,
          publicKey,
          transferAmountInUnits,
          mintInfo.decimals,
          [],
          TOKEN_EXTENSIONS_PROGRAM_ID
        )
      );

      // Set fee payer and get recent blockhash
      transaction.feePayer = publicKey;
      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash("confirmed");
      transaction.recentBlockhash = blockhash;

      console.log("Transaction prepared:", transaction);
      console.log("Fee payer:", transaction.feePayer?.toString());
      console.log("Blockhash:", transaction.recentBlockhash);

      // Try to simulate the transaction first
      try {
        console.log("Simulating transaction...");
        const simulationResult = await connection.simulateTransaction(
          transaction
        );
        console.log("Simulation result:", simulationResult);

        if (simulationResult.value.err) {
          throw new Error(
            `Transaction simulation failed: ${JSON.stringify(
              simulationResult.value.err
            )}`
          );
        }
        console.log("✅ Simulation successful");
      } catch (simError) {
        console.error("Simulation error:", simError);
        throw new Error(`Transaction simulation failed: ${simError}`);
      }

      // Send transaction with proper options
      console.log("Sending transaction...");
      const signature = await sendTransaction(transaction, connection, {
        maxRetries: 3,
        preflightCommitment: "confirmed",
        skipPreflight: false,
      });

      console.log("Transaction sent:", signature);

      // Wait for confirmation with timeout
      const confirmation = await connection.confirmTransaction(
        {
          signature,
          blockhash,
          lastValidBlockHeight,
        },
        "confirmed"
      );

      if (confirmation.value.err) {
        throw new Error(
          `Transaction failed: ${JSON.stringify(confirmation.value.err)}`
        );
      }

      console.log("✅ Transaction confirmed");

      toast({
        title: "Transfer successful!",
        description: `Sent ${amount} tokens to ${transferAddress.slice(
          0,
          8
        )}...${transferAddress.slice(-8)}`,
        variant: "default",
      });

      // Clear form and refresh balances
      setTransferAmount("");
      setTransferAddress("");
      await refreshBalances();
    } catch (error) {
      console.error("Transfer error:", error);

      let errorMessage = "Transfer failed";
      if (error instanceof Error) {
        console.error("Error details:", {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });

        if (
          error.message.includes("User rejected") ||
          error.message.includes("user rejected")
        ) {
          errorMessage = "Transaction was rejected by user";
        } else if (error.message.includes("insufficient funds")) {
          errorMessage = "Insufficient SOL for transaction fees";
        } else if (error.message.includes("Account not found")) {
          errorMessage = "Token account not found";
        } else if (error.message.includes("Unexpected error")) {
          errorMessage =
            "Wallet connection error. Please try reconnecting your wallet.";
        } else if (error.message.includes("simulation failed")) {
          errorMessage =
            "Transaction validation failed. Please check your inputs.";
        } else if (error.message.includes("Blockhash not found")) {
          errorMessage = "Network error. Please try again.";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Transfer failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setTransferring(false);
    }
  };

  if (!connected) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16"
      >
        <Wallet className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-2xl font-bold mb-2">Connect Your Wallet</h2>
        <p className="text-muted-foreground mb-6">
          Connect your wallet to view balances and manage tokens
        </p>
      </motion.div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="d-flex align-items-center justify-content-center gap-4 mb-2">
          <h1 className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            💰 User Portal
          </h1>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshBalances}
            disabled={refreshing}
            className="ml-4  btn main-btn  w-100 w-full "
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
        <p className="text-muted-foreground">
          Manage your tokens and explore DeFi opportunities
        </p>
      </motion.div>

      {/* Balance Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="cards-main-section section_03 gap-6 ">
          <div className="container">
            <div className="row">
              <div className="col-md-4">
                <Card className="bg-gradient-card shadow-card card_02 ">
                  <CardHeader className="d-flex flex-row align-items-center justify-content-between gap-2 pb-2">
                    <CardTitle className="text-sm font-medium mb-2">
                      SOL Balance
                    </CardTitle>
                    <Coins className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {solBalance?.balance.toFixed(4) || "0"} SOL
                    </div>
                    <p className="text-xs text-muted-foreground">
                      ≈ ${solBalance?.usdValue.toFixed(2) || "0.00"} USD
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* SOL Balance */}
              <div className="col-md-4">
                {/* Token Balance */}
                <Card className="bg-gradient-card shadow-card card_02 ">
                  <CardHeader className="d-flex flex-row align-items-center justify-content-between gap-2 pb-2">
                    <CardTitle className="text-sm font-medium mb-2">
                      Token Balance
                    </CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-accent">
                      {tokenBalance?.balance.toLocaleString() || "0"} SPL
                    </div>
                    <p className="text-xs text-muted-foreground">
                      ≈ ${tokenBalance?.usdValue.toFixed(2) || "0.00"} USD
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="col-md-4">
                {/* Total Supply */}
                <Card className="bg-gradient-card shadow-card card_02 ">
                  <CardHeader className="d-flex flex-row align-items-center justify-content-between gap-2 pb-2">
                    <CardTitle className="text-sm font-medium mb-2">
                      Total Supply
                    </CardTitle>
                    <Gift className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-500">
                      {tokenInfo?.supply.toLocaleString() || "0"} SPL
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {tokenInfo?.decimals || 0} decimals
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Action Cards */}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="action-cards-section section_03 ">
          <div className="container">
            <div className="row">
              <div className="col-md-4">
                {/* Transfer Tokens */}
                <Card className="card_02">
                  <CardHeader>
                    <CardTitle className="d-flex align-items-center gap-2 text-primary">
                      <Send className="h-5 w-5" />
                      Transfer
                    </CardTitle>
                    <CardDescription>
                      Send tokens to another wallet
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 form-group">
                      <Label htmlFor="transfer-address">
                        Recipient Address
                      </Label>
                      <Input
                        id="transfer-address"
                        placeholder="Enter wallet address..."
                        value={transferAddress}
                        onChange={(e) => setTransferAddress(e.target.value)}
                        className="bg-background font-mono text-xs"
                      />
                    </div>
                    <div className="space-y-2 form-group">
                      <Label htmlFor="transfer-amount">Amount</Label>
                      <div className="d-flex gap-2">
                        <Input
                          id="transfer-amount"
                          type="number"
                          placeholder="0.00"
                          value={transferAmount}
                          onChange={(e) => setTransferAmount(e.target.value)}
                          className="bg-background flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="max-btn w-100 w-full "
                          onClick={() =>
                            setTransferAmount(
                              tokenBalance?.balance.toString() || "0"
                            )
                          }
                          disabled={!tokenBalance || tokenBalance.balance === 0}
                        >
                          Max
                        </Button>
                      </div>
                      {tokenBalance && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Available: {tokenBalance.balance.toLocaleString()} SPL
                        </p>
                      )}
                    </div>
                    <Button
                      variant="default"
                      className="w-full btn-style w-100 w-full "
                      onClick={handleTransfer}
                      disabled={
                        !transferAddress || !transferAmount || transferring
                      }
                    >
                      {transferring ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2 w-100 w-full" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {transferring ? "Sending..." : "Send Tokens"}
                    </Button>
                  </CardContent>
                </Card>
              </div>
              <div className="col-md-4">
                {/* Buy Tokens */}
                <Card className="bg-gradient-card  card_02">
                  <CardHeader>
                    <CardTitle className="d-flex align-items-center gap-2 text-success">
                      <DollarSign className="h-5 w-5" />
                      Buy Tokens
                    </CardTitle>
                    <CardDescription>
                      Swap SOL for tokens on Jupiter
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center py-4">
                      <p className="text-sm text-muted-foreground mb-4">
                        Use Jupiter DEX to swap SOL for tokens with the best
                        rates
                      </p>
                      <Badge variant="secondary" className="mb-4 badge">
                        Best rates guaranteed
                      </Badge>
                    </div>
                    <Button
                      className="w-full btn-style"
                      onClick={handleJupiterSwap}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Jupiter
                    </Button>
                  </CardContent>
                </Card>
              </div>
              <div className="col-md-4">
                {/* Claim Rewards */}
                <Card className="bg-gradient-card  card_02">
                  <CardHeader>
                    <CardTitle className="d-flex align-items-center gap-2 text-warning">
                      <Gift className="h-5 w-5" />
                      Staking
                    </CardTitle>
                    <CardDescription>
                      Earn rewards by staking your tokens
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center py-4">
                      <div className="text-2xl font-bold text-warning mb-2">
                        0.00 SPL
                      </div>
                      <p className="text-xs text-muted-foreground mb-4">
                        Pending rewards
                      </p>
                      <Badge variant="outline" className="mb-4 badge">
                        Coming soon
                      </Badge>
                    </div>
                    <Button
                      variant="warning"
                      className="w-full btn-style"
                      onClick={handleClaimRewards}
                      disabled
                    >
                      <Gift className="h-4 w-4 mr-2" />
                      Claim Rewards
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Airdrop Application */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="airpod-section">
          <AirdropApplicationForm
            walletAddress={publicKey?.toString()}
            onApplicationSubmitted={(application) => {
              toast({
                title: "Application Submitted!",
                description: `Your airdrop application has been submitted successfully. Status: ${application.status}`,
                variant: "default",
              });
            }}
          />
        </div>
      </motion.div>

      {/* Account Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="account-information-section">
          <div className="container">
            <Card className="mb-3 card-main-wrapper">
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Account Information
                </CardTitle>
                <CardDescription>
                  Your wallet details and token account status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 main-info-wrapper">
                  <div className="d-flex justify-content-between align-items-center text-wrapper">
                    <span className="text-sm font-medium">Wallet Address:</span>
                    <span className="text-sm font-mono text-muted-foreground">
                      {publicKey?.toString().slice(0, 8)}...
                      {publicKey?.toString().slice(-8)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center text-wrapper">
                    <span className="text-sm font-medium">Token Account:</span>
                    <Badge
                      variant={
                        tokenBalance && tokenBalance.balance > 0
                          ? "default"
                          : "secondary"
                      }
                      className="badge"
                    >
                      {tokenBalance && tokenBalance.balance > 0
                        ? "Active"
                        : "Not Created"}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center text-wrapper">
                    <span className="text-sm font-medium">Network:</span>
                    <Badge variant="outline" className="badge">
                      Mainnet
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UserPortal;
